import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { Effect, Actions } from '@ngrx/effects';

// Rxjs
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/exhaustMap';
import { of } from 'rxjs/observable/of';
import { Observable } from 'rxjs/Observable';

// Services
import { AccountService } from '../services/account.service';

// Actions
import * as Auth from '../../auth/actions/auth.actions';
import * as Account from '../actions/account.actions';

@Injectable()
export class AccountEffects {

  // Constructor
  constructor(
    private actions$: Actions,
    private accountService: AccountService
  ) {}

  // ================= GENERAL SETTINGS =================
  // ====================================================
  // Checking email
  @Effect()
  checkEmail$ = this.actions$
    .ofType(Account.EMAIL_VERIFICATION)
    .map((action: Account.EmailVerification) => action.payload)
    .exhaustMap(data =>
      this.accountService
        .emailVerification({ email: data.email })
        .map(() => new Account.SendValidationCodeToEmail({ email: data.email }))
        .catch(error => of(new Account.EmailVerificationFail(error.errors.message)))
    );
  // Update client account
  @Effect()
  updateClientAccount$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_ACCOUNT)
    .map((action: Account.UpdateClientAccount) => action.payload)
    .exhaustMap(data =>
      this.accountService
        .updateClientAccount(data)
        .map(() => new Account.UpdateClientAccountSuccess())
    );
  // Update client account success
  @Effect()
  updateClientAccountSuccess$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_ACCOUNT_SUCCESS)
    .map(() => new Auth.UserInformationRefresh());
  // Update client phone number
  @Effect()
  updateClientPhone$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_PHONE)
    .map((action: Account.UpdateClientPhone) => action.payload)
    .exhaustMap(phone =>
      this.accountService
        .updateClientPhone(phone)
        .map(() => new Account.UpdateClientPhoneSuccess())
    );
  // Update client phone success
  @Effect()
  updateClientPhoneSuccess$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_PHONE_SUCCESS)
    .map(() => new Auth.UserInformationRefresh());
  // Update client phone 2FA code
  @Effect()
  updateClientPhone2FACode$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_PHONE_2FA_CODE)
    .exhaustMap((action: Account.UpdateClientPhone2FACode) =>
      this.accountService
        .updateClientPhone(action.payload)
        .map(() => new Account.UpdateClientPhone2FACodeSuccess())
    );
  // Update client phone 2FA
  @Effect()
  updateClientPhone2FA$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_PHONE_2FA)
    .map((action: Account.UpdateClientPhone2FA) => action.payload)
    .exhaustMap(phone =>
      this.accountService
        .updateClientPhone2FA(phone)
        .map(() => new Account.UpdateClientPhone2FASuccess())
        .catch(error => {
          return of(new Account.UpdateClientPhone2FAFail(error.errors.message));
        })
    );
  // Make a 2FA phone call
  @Effect()
  tfaPhoneCall$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_PHONE_2FA_CALL)
    .map((action: Account.UpdateClientPhone2FACall) => action.payload)
    .exhaustMap(phone =>
      this.accountService
        .tfaPhoneCall(phone)
        .map(() => new Account.UpdateClientPhone2FACallSuccess())
        .catch(error => {
          console.error('tfaPhoneCall$  error=', error);
          return of(new Account.UpdateClientPhone2FACallFail(error.errors.message));
        })
    );
  // Disable 2FA to remove phone number
  @Effect()
  disable2FAToRemovePhone$ = this.actions$
    .ofType(Account.DISABLING_2FA_TO_REMOVE_PHONE)
    .map((action: Account.Disabling2FAToRemovePhone) => action.peyload)
    .map(data =>
      new Account.UpdateClient2FAConfigurationRemovePhone(data)
    );
  // Send validation code to email address
  @Effect({ dispatch: false })
  sendCodeToEmail$ = this.actions$
    .ofType(Account.SEND_CODE_TO_EMAIL_ADDRESS)
    .map((action: Account.SendValidationCodeToEmail) => action.payload)
    .exhaustMap(email =>
      this.accountService
        .sendCodeToEmailAddress(email)
        .catch(error => of())
    );
  // Validate email address
  @Effect()
  validateEmailAddress$ = this.actions$
    .ofType(Account.VALIDATE_EMAIL_ADDRESS)
    .map((action: Account.ValidateEmailAddress) => action.payload)
    .exhaustMap(data =>
      this.accountService
        .validateEmailAddress(data.email, data.email_code)
        .map(() => new Account.ValidateEmailAddressSuccess())
        .catch(error => of(new Account.UpdateClientPhone2FAFail(error.errors.message)))
    );

  // ================= SECURITY SETTING =================
  // ====================================================
  // Change client password
  @Effect()
  changeClientPassword$ = this.actions$
    .ofType(Account.CHANGE_CLIENT_PASSWORD)
    .map((action: Account.ChangeClientPassword) => action.payload)
    .exhaustMap(values =>
      this.accountService
        .changeClientPassword(values)
        .map(() => new Account.ChangeClientPasswordSuccess())
        .catch(error => of(new Account.ChangeClientPasswordFail(error.errors.message)))
    );
  // Update client 2FA configuration
  @Effect()
  updateClient2FAConfiguration$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_2FA_CONFIGURATION)
    .map((action: Account.UpdateClient2FAConfiguration) => action.payload)
    .exhaustMap(status =>
      this.accountService
        .update2FAConfiguration(status)
        .map((response: any) => {
          console.log('account.effects.ts:updateClient2FAConfiguration$  status=', status, '  response=', response);
          console.log('account.effects.ts:updateClient2FAConfiguration$  status.factor_authentication=', status.factor_authentication, '  status.usrTfa=', status.usrTfa, '  status=', status, '  response=', response);
          // tslint:disable-next-line:no-bitwise
          if ((status.factor_authentication & 0x01) && !(status.usrTfa & 0x01))
            return new Account.UpdateClient2FAConfigurationSuccess();
          // tslint:disable-next-line:no-bitwise
          else if ((status.factor_authentication & 0x02) && !(status.usrTfa & 0x02))
            return new Account.UpdateClient2FAConfigurationSuccess();
          // tslint:disable-next-line:no-bitwise
          else if ((status.factor_authentication & 0x04) && !(status.usrTfa & 0x04))
            return new Account.UpdateClient2FAConfigurationSuccess();
          // tslint:disable-next-line:no-bitwise
          else if (!(status.factor_authentication & 0x02) && (status.usrTfa & 0x02)) {
            console.log('account.effects.ts:updateClient2FAConfiguration$  response.mfaUri=', response.tfa_uri);
            return new Account.UpdateClient2FAUri(response.tfa_uri);
          } else return new Auth.Dummy();
        })
    );
  // Update client 2FA configuration then remove phone
  @Effect()
  updateClient2FAConfigurationRemovePhone$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_2FA_CONFIGURATION_REMOVE_PHONE)
    .map((action: Account.UpdateClient2FAConfigurationRemovePhone) => action.payload)
    .exhaustMap(data =>
      this.accountService
        .update2FAConfiguration({ usr_tfa: data.usr_tfa })
        .map(() => new Account.UpdateClientPhone(data.phone))
    );
  // Update client 2FA configuration success
  @Effect()
  updateClient2FAConfigurationSuccess$ = this.actions$
    .ofType(Account.UPDATE_CLIENT_2FA_CONFIGURATION_SUCCESS)
    .map(() => new Auth.UserInformationRefresh());
  // Verify client 2FA configuration
  @Effect()
  verifyClient2FAConfiguration$ = this.actions$
    .ofType(Account.VERIFY_CLIENT_2FA_CONFIGURATION)
    .map((action: Account.VerifyClient2FAConfiguration) => action.payload)
    .exhaustMap(data =>
      this.accountService
        .verify2FAConfiguration(data.tfaCode, data.tfaType)
        .map(() => new Account.VerifyClient2FAConfigurationSuccess())
        .catch(error => {
          return of(new Account.VerifyClient2FAConfigurationFail(error.errors.message));
        })
    );
  // Should refresh user information
  @Effect()
  verifyClient2FAConfigurationSuccess$ = this.actions$
    .ofType(
      Account.LICENSE_AGREEMENT_SUCCESS,
      Account.CHANGE_CLIENT_PASSWORD_SUCCESS,
      Account.VALIDATE_EMAIL_ADDRESS_SUCCESS,
      Account.VERIFY_CLIENT_2FA_CONFIGURATION_SUCCESS,
    )
    .map(() => new Auth.UserInformationRefresh());

  // ================ LICENSE AGREEMENT =================
  // ====================================================
  // Get license agreement
  @Effect()
  getLicenseAgreement$ = this.actions$
    .ofType(Account.GET_LICENSE_AGREEMENT)
    .map((action: Account.GetLicenseAgreement) => action.payload)
    .exhaustMap(lang =>
      this.accountService
        .getLicenseAgreement(lang)
        .map((response: any) => new Account.GetLicenseAgreementSuccess(response.data[0].description))
        .catch(() => of())
    );

  // Get Privacy
  @Effect()
  getPrivacy$ = this.actions$
    .ofType(Account.GET_PRIVACY)
    .map((action: Account.GetPrivacy) => action.payload)
    .exhaustMap(lang =>
      this.accountService
        .getPrivacy(lang)
        .map((response: any) => new Account.GetPrivacySuccess(response.data[0]))
        .catch(() => of())
    );

  // Get Terms
  @Effect()
  getTerms$ = this.actions$
    .ofType(Account.GET_TERMS)
    .map((action: Account.GetTerms) => action.payload)
    .exhaustMap(lang =>
      this.accountService
        .getTerms(lang)
        .map((response: any) => new Account.GetTermsSuccess(response.data[0]))
        .catch(() => of())
    );

  // License agreement
  @Effect()
  licenseAgreement$ = this.actions$
    .ofType(Account.LICENSE_AGREEMENT)
    .map((action: Account.LicenseAgreement) => action.payload)
    .exhaustMap((status) =>
      this.accountService
        .licenseAgreement(status)
        .map(() => new Account.LicenseAgreementSuccess())
        .catch(() => of())
    );

  // ================= CLIENT PROJECTS ==================
  // ====================================================
  // Get client projects
  @Effect()
  getClientProjects$ = this.actions$
    .ofType(Account.GET_CLIENT_PROJECTS)
    .exhaustMap(() =>
      this.accountService
        .getClientProjects()
        .map((response: any) => new Account.GetClientProjectsSuccess(response.data[0]))
        .catch(() => of())
    );

}



// WEBPACK FOOTER //
// ./src/app/my-account/effects/account.effects.ts